import styles from "./AdditionalAmenitiesModal.module.scss";
import { TiMinus, TiPlus } from "react-icons/ti";
import { Switch } from "react-animated-switch";
import Modal from "../../Common/Modal/Modal";
import React from "react";

type ParkingOption = {
  parkingId: number;
  amenityName: string;
  noofvehicle: number;
  availableNoofvehicle: number;
  cost: number;
  terms: string;
  image: string;
  amenityId: number;
};

type ParkingSelection = {
  parkingId: string;
  noOfVehicle: number;
  notifyParking: boolean;
};

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  parkingOptions?: ParkingOption[];
  parkingSelections: ParkingSelection[];
  setParkingSelections: React.Dispatch<
    React.SetStateAction<ParkingSelection[]>
  >;
}

const AdditionalAmenitiesModal = (props: Props) => {
  const {
    isOpen,
    setIsOpen,
    parkingOptions,
    parkingSelections,
    setParkingSelections,
  } = props;

  const getSel = (parkingId: number) =>
    parkingSelections.find((s) => s.parkingId === String(parkingId)) ?? {
      parkingId: String(parkingId),
      noOfVehicle: 0,
      notifyParking: false,
    };

  const updateSel = (parkingId: number, patch: Partial<ParkingSelection>) => {
    setParkingSelections((prev) => {
      const id = String(parkingId);

      const i = prev.findIndex((s) => s.parkingId === id);

      const cur =
        i >= 0
          ? prev[i]
          : {
              parkingId: id,
              noOfVehicle: 0,
              notifyParking: false,
            };

      const next = { ...cur, ...patch };

      return i >= 0
        ? prev.map((s, j) => (j === i ? next : s))
        : [...prev, next];
    });
  };

  const changeCount = (opt: ParkingOption, delta: number) => {
    const s = getSel(opt.parkingId);
    const max = opt.noofvehicle ?? 0;
    const next = s.noOfVehicle + delta;

    if (next >= 0 && next <= max) {
      updateSel(opt.parkingId, { noOfVehicle: next });
    }
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen} showCloseIcon={true}>
      <div className={styles.additionalAmenitiesModalContainer}>
        <div className={styles.headingContainer}>
          <p className={styles.heading}>Select Additional Amenities</p>
        </div>
        <div className={styles.parkingGroupContainer}>
          {parkingOptions?.map((opt) => {
            const s = getSel(opt.parkingId);

            const overLimit = s.noOfVehicle > (opt.availableNoofvehicle ?? 0);

            const noneAvailable = (opt.availableNoofvehicle ?? 0) === 0;

            const showNotifySwitch = overLimit;

            return (
              <div key={opt.parkingId} className={styles.parkingGroup}>
                {(overLimit || noneAvailable) && (
                  <div className={styles.notAvailableContainer}>
                    <p className={styles.label}>Unavailable</p>
                  </div>
                )}
                {opt.amenityName && (
                  <p className={styles.parkingGroupHeading}>
                    {opt.amenityName}
                  </p>
                )}

                <div className={styles.parkingContainer}>
                  <div className={styles.contentContainer}>
                    <div className={styles.contentInnerContainer}>
                      <div className={styles.labelContainer}>
                        <p className={styles.label}>Parking Spaces Available ({opt?.availableNoofvehicle ?? 0})</p>
                      </div>

                      <div className={styles.counterContainer}>
                        <button
                          className={styles.counterButton}
                          disabled={noneAvailable}
                          onClick={() => changeCount(opt, -1)}
                        >
                          <TiMinus />
                        </button>

                        <p className={styles.counterValue}>{s.noOfVehicle}</p>

                        <button
                          className={styles.counterButton}
                          disabled={noneAvailable}
                          onClick={() => changeCount(opt, 1)}
                        >
                          <TiPlus />
                        </button>
                      </div>

                      <div className={styles.priceContainer}>
                        <p>$ {(opt.cost ?? 0) * s.noOfVehicle}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {showNotifySwitch && (
                  <div className={styles.noticeContainer}>
                    <div className={styles.switchContainer}>
                      <Switch
                        inActiveTrackColor="#DFDFDF"
                        activeTrackColor="#C0F496"
                        inActiveThumbColor="#000000"
                        activeThumbColor="#000000"
                        checked={s.notifyParking}
                        onChange={(val) =>
                          updateSel(opt.parkingId, {
                            notifyParking: val,
                          })
                        }
                      />
                    </div>

                    <p className={styles.label}>
                      Notify me when parking is available
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className={styles.buttonContainer}>
          <button className={styles.button} onClick={() => setIsOpen(false)}>
            Continue
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AdditionalAmenitiesModal;
